import type { ReactNode } from 'react';
import { T, useGT, useLocale } from 'gt-react';
import type { LineUsage } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useProductSalesHistory } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import {
  lineSalesTable,
  monthLabel,
  type LineSalesRow,
  type LineSalesTable,
} from '../utils/lineSalesTable';
import { formatQuantity } from '../utils/lineFacts';
import { LineUsageCell } from './LineUsageCell';

/** Two years of history: the twelve months shown and the year before them. */
const MONTHS_BACK = 24;

function RowLabel({ row }: { row: LineSalesRow['key'] }) {
  if (row === 'prior') {
    return (
      <T context="line sales tab row: sales in the same months a year earlier">
        Year before
      </T>
    );
  }
  if (row === 'recent') {
    return (
      <T context="line sales tab row: sales in the last twelve months">
        Last 12 mo
      </T>
    );
  }
  return (
    <T context="line sales tab row: forecast demand for this month and the next eleven">
      Forecast
    </T>
  );
}

function Cell({
  children,
  current = false,
}: {
  children: ReactNode;
  current?: boolean;
}) {
  return (
    <td
      className={cn(
        'px-1.5 py-0.5 text-right tabular-nums',
        current && 'bg-muted font-semibold',
      )}
    >
      {children}
    </td>
  );
}

function SalesMonthTable({ table }: { table: LineSalesTable }) {
  const gt = useGT();
  const locale = useLocale();
  const last = table.months.length - 1;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="text-muted-foreground">
          <tr>
            <th>
              <span className="sr-only">
                <T context="line sales tab: header of the row-label column">
                  Period
                </T>
              </span>
            </th>
            {table.months.map((month, i) => (
              <th
                key={month}
                className={cn(
                  'px-1.5 py-0.5 text-right font-medium',
                  i === last && 'bg-muted text-foreground',
                )}
                title={
                  i === last
                    ? gt('This month, to date', {
                        context: 'line sales tab: the current month column',
                      })
                    : undefined
                }
              >
                {monthLabel(month, locale)}
              </th>
            ))}
            <th className="px-1.5 py-0.5 text-right font-medium">
              <T context="line sales tab column: the row's total">Total</T>
            </th>
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row) => (
            <tr
              key={row.key}
              className={cn(row.key !== 'recent' && 'text-muted-foreground')}
            >
              <th className="whitespace-nowrap py-0.5 pr-2 text-left font-normal">
                <RowLabel row={row.key} />
              </th>
              {row.cells.map((cell, i) => (
                <Cell
                  key={table.months[i]}
                  current={row.key === 'recent' && i === last}
                >
                  {formatQuantity(cell)}
                </Cell>
              ))}
              <Cell>{formatQuantity(row.total)}</Cell>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsageFooter({ usage }: { usage: LineUsage | null }) {
  return (
    <div className="flex gap-4 text-muted-foreground">
      <span className="flex items-baseline gap-1">
        <T context="line sales tab: 12-month customer sales at this location">
          Usage 12 mo
        </T>
        <span className="text-foreground">
          <LineUsageCell usage={usage} />
        </span>
      </span>
      <span className="flex items-baseline gap-1">
        <T context="line sales tab: 12-month sales at locations that restock from this one">
          Dependent
        </T>
        <span className="text-foreground">
          <LineUsageCell usage={usage} part="dependent" />
        </span>
      </span>
    </div>
  );
}

function SalesLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <p role="alert" className="flex items-center gap-2 text-muted-foreground">
      <T context="line sales tab: the sales history request failed">
        Couldn't load sales history.
      </T>
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0 text-xs"
        onClick={onRetry}
      >
        <T context="line sales tab: try loading the sales history again">
          Retry
        </T>
      </Button>
    </p>
  );
}

/**
 * The selected line's sales by month at the destination (GEN-3285): the last
 * twelve months against the year before and the forecast, with this month
 * (month to date) marked, and the 12-month usage beside it.
 */
export function LineSalesTab({
  productV3Key,
  warehouseId,
  usage,
  now = new Date(),
}: {
  productV3Key: string;
  warehouseId: string;
  usage: LineUsage | null;
  now?: Date;
}) {
  const { data, isLoading, isError, refetch } = useProductSalesHistory({
    productV3Key,
    warehouseId,
    monthsBack: MONTHS_BACK,
  });
  if (isLoading || (!isError && data === undefined)) return null;
  return (
    <div className="space-y-2 p-3 text-xs">
      {data === undefined ? (
        // The board's queries don't retry on their own; usage still shows.
        <SalesLoadError onRetry={() => void refetch()} />
      ) : (
        <SalesMonthTable
          table={lineSalesTable(data.salesHistory, data.forecast, now)}
        />
      )}
      <UsageFooter usage={usage} />
    </div>
  );
}
